/**
 * CreditsPage - Main page for credit management
 * Redesigned with same layout as Account page (dark mode, sidebar menu)
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  CreditCard, History, LayoutDashboard, RefreshCw, Check, AlertTriangle,
  Package, TrendingUp, TrendingDown, Loader2, ChevronRight, Wallet,
  Zap, Crown, Server, Globe, Building2, Coins, Receipt, ShoppingCart,
  ArrowUpRight, ArrowDownRight, Clock, Calendar, Star, Gift
} from 'lucide-react';
import { useCredits, useCreditPlans, useCreditPacks, useCreditTransactions, useCreditCosts } from '../../hooks/useCredits';
import { toast } from '../../utils/toastUtils';
import api from '../../api/client';
import { useI18n } from '../../i18n';

// Plan badge styles (matching Account.jsx)
const PLAN_STYLES = {
  free: { color: 'text-gray-400', bg: 'bg-gray-500/10', border: 'border-gray-500/30', icon: Globe },
  pro: { color: 'text-brand-green', bg: 'bg-brand-green/10', border: 'border-brand-green/30', icon: Crown },
  entreprise: { color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/30', icon: Building2 },
  groupe: { color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/30', icon: Server },
  dedicated: { color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30', icon: Zap }
};

// Credit Summary Card (dark design)
function CreditSummaryCard({ summary, loading }) {
  const { t } = useI18n();

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="animate-spin text-brand-green" size={32} />
      </div>
    );
  }

  const totalCredits = summary?.total_credits || 0;
  const monthlyCredits = summary?.monthly_credits || 0;
  const packCredits = summary?.pack_credits || 0;

  return (
    <div className="space-y-6">
      {/* Main Balance */}
      <div className="liquid-glass rounded-xl p-6 border border-brand-green/30">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 rounded-xl bg-brand-green/20 flex items-center justify-center">
            <Wallet className="w-7 h-7 text-brand-green" />
          </div>
          <div>
            <h3 className="text-gray-400 text-sm">{t('account.creditsSection.balance')}</h3>
            <div className="text-4xl font-bold text-white">{totalCredits.toLocaleString('fr-FR')}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Monthly Credits */}
          <div className="bg-[#111111] rounded-xl p-4 border border-gray-800">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-brand-green" />
              <span className="text-gray-400 text-sm">{t('account.creditsSection.monthlyCredits')}</span>
            </div>
            <div className="text-2xl font-bold text-white">{monthlyCredits.toLocaleString('fr-FR')}</div>
            {summary?.monthly_credits_expire_at && (
              <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {t('account.creditsSection.expires')}: {new Date(summary.monthly_credits_expire_at).toLocaleDateString('fr-FR')}
              </div>
            )}
          </div>

          {/* Pack Credits */}
          <div className="bg-[#111111] rounded-xl p-4 border border-gray-800">
            <div className="flex items-center gap-2 mb-2">
              <Package className="w-4 h-4 text-purple-400" />
              <span className="text-gray-400 text-sm">{t('account.creditsSection.packCredits')}</span>
            </div>
            <div className="text-2xl font-bold text-white">{packCredits.toLocaleString('fr-FR')}</div>
            <div className="text-xs text-gray-500 mt-1">{t('account.creditsSection.noExpiration')}</div>
          </div>
        </div>
      </div>

      {/* Current Plan */}
      {summary?.subscription_plan_name && (
        <div className="liquid-glass rounded-xl p-4 border border-brand-green/20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Crown className="w-5 h-5 text-brand-green" />
            <div>
              <span className="text-gray-400 text-sm">{t('account.creditsSection.currentPlan')}: </span>
              <span className="font-semibold text-white">{summary.subscription_plan_name}</span>
            </div>
          </div>
          {summary.subscription_status && (
            <span className={`text-xs px-3 py-1.5 rounded-full ${
              summary.subscription_status === 'active'
                ? 'bg-brand-green/20 text-brand-green border border-brand-green/30'
                : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
            }`}>
              {summary.subscription_status === 'active' ? t('account.creditsSection.statusActive') : summary.subscription_status}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// Plans Section (dark design)
function PlansSection({ currentPlan, onSelect }) {
  const { t } = useI18n();
  const { plans, loading, error } = useCreditPlans();

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="animate-spin text-brand-green" size={32} />
      </div>
    );
  }

  if (error) {
    return <div className="p-4 bg-red-500/10 text-red-400 rounded-xl border border-red-500/30">{error}</div>;
  }

  return (
    <div>
      <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
        <Crown className="w-5 h-5 text-brand-green" />
        {t('account.creditsSection.monthlyPlans')}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {plans.map((plan) => {
          const style = PLAN_STYLES[plan.slug] || PLAN_STYLES.free;
          const PlanIcon = style.icon;
          const isCurrentPlan = currentPlan === plan.slug;

          return (
            <div
              key={plan.slug}
              className={`liquid-glass rounded-xl p-6 border-2 transition-all ${
                isCurrentPlan
                  ? 'border-brand-green/50 bg-brand-green/5'
                  : 'border-gray-800 hover:border-gray-700'
              }`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-lg ${style.bg} flex items-center justify-center flex-shrink-0`}>
                  <PlanIcon className={`w-5 h-5 ${style.color}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="text-lg font-bold text-white truncate" title={plan.name}>{plan.name}</h4>
                  {isCurrentPlan && (
                    <span className="text-xs text-brand-green">{t('account.creditsSection.currentPlan')}</span>
                  )}
                </div>
              </div>

              <div className="text-3xl font-bold text-white mb-2">
                {plan.monthly_price_eur === 0 ? t('account.creditsSection.free') : `${plan.monthly_price_eur}€`}
                {plan.monthly_price_eur > 0 && <span className="text-sm font-normal text-gray-400">{t('account.creditsSection.perMonth')}</span>}
              </div>

              <div className="text-lg mb-4">
                <span className="font-semibold text-brand-green">{plan.monthly_credits.toLocaleString('fr-FR')}</span>
                <span className="text-gray-400"> {t('account.creditsSection.creditsPerMonth')}</span>
              </div>

              <ul className="text-sm text-gray-300 space-y-2 mb-6">
                <li className="flex items-center gap-2">
                  <Check size={16} className="text-brand-green flex-shrink-0" />
                  <span className="truncate">{plan.max_workspaces === -1 ? t('account.creditsSection.unlimitedWorkspaces') : `${plan.max_workspaces} workspace${plan.max_workspaces > 1 ? 's' : ''}`}</span>
                </li>
                {plan.features?.scans_externes && (
                  <li className="flex items-center gap-2">
                    <Check size={16} className="text-brand-green flex-shrink-0" />
                    <span className="truncate">{t('account.creditsSection.externalScans')}</span>
                  </li>
                )}
                {plan.features?.scans_internes && (
                  <li className="flex items-center gap-2">
                    <Check size={16} className="text-brand-green flex-shrink-0" />
                    <span className="truncate">{t('account.creditsSection.internalScans')}</span>
                  </li>
                )}
                {plan.features?.scans_ad && (
                  <li className="flex items-center gap-2">
                    <Check size={16} className="text-brand-green flex-shrink-0" />
                    <span className="truncate">{t('account.creditsSection.adScans')}</span>
                  </li>
                )}
              </ul>

              <button
                onClick={() => onSelect(plan)}
                disabled={isCurrentPlan}
                className={`w-full py-3 rounded-xl font-medium transition-all ${
                  isCurrentPlan
                    ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                    : 'bg-brand-green text-black hover:bg-brand-green/90'
                }`}
              >
                {isCurrentPlan ? t('account.creditsSection.currentPlan') : t('account.creditsSection.select')}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Packs Section (dark design)
function PacksSection({ onPurchase }) {
  const { t } = useI18n();
  const { packs, loading, error } = useCreditPacks();

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="animate-spin text-brand-green" size={32} />
      </div>
    );
  }

  if (error) {
    return <div className="p-4 bg-red-500/10 text-red-400 rounded-xl border border-red-500/30">{error}</div>;
  }

  return (
    <div className="mt-8">
      <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
        <Package className="w-5 h-5 text-purple-400" />
        {t('account.creditsSection.creditPacks')}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {packs.map((pack) => (
          <div key={pack.slug} className="liquid-glass rounded-xl p-6 border border-gray-800 hover:border-purple-500/30 transition-all">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                <Gift className="w-5 h-5 text-purple-400" />
              </div>
              <h4 className="text-lg font-bold text-white truncate" title={pack.name}>{pack.name}</h4>
            </div>

            <div className="text-3xl font-bold text-purple-400 mb-2">
              {pack.credits.toLocaleString('fr-FR')} <span className="text-sm font-normal text-gray-400">{t('account.creditsSection.credits')}</span>
            </div>

            <div className="text-xl font-semibold text-white mb-2">
              {pack.price_eur.toLocaleString('fr-FR')}€
              {pack.discount_percentage > 0 && (
                <span className="ml-2 text-sm bg-brand-green/20 text-brand-green px-2 py-1 rounded-full">
                  -{pack.discount_percentage}%
                </span>
              )}
            </div>

            <p className="text-sm text-gray-400 mb-4 line-clamp-2" title={pack.description}>{pack.description}</p>

            <div className="text-xs text-gray-500 mb-4 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {t('account.creditsSection.validity')}: {pack.validity_months} {t('account.creditsSection.months')}
            </div>

            <button
              onClick={() => onPurchase(pack)}
              className="w-full py-3 bg-purple-500/20 text-purple-400 rounded-xl font-medium hover:bg-purple-500/30 transition-all border border-purple-500/30"
            >
              <ShoppingCart className="w-4 h-4 inline mr-2" />
              {t('account.creditsSection.buy')}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// Costs Section (dark design)
function CostsSection() {
  const { t } = useI18n();
  const { costs, loading, error } = useCreditCosts();

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="animate-spin text-brand-green" size={32} />
      </div>
    );
  }

  if (error) {
    return <div className="p-4 bg-red-500/10 text-red-400 rounded-xl border border-red-500/30">{error}</div>;
  }

  // Group by category
  const costsByCategory = costs.reduce((acc, cost) => {
    if (!acc[cost.category]) acc[cost.category] = [];
    acc[cost.category].push(cost);
    return acc;
  }, {});

  const categoryLabels = {
    scan: t('account.creditsSection.scans'),
    leak: t('account.creditsSection.leakDetection'),
    audit: t('account.creditsSection.audits'),
    report: t('account.creditsSection.reports')
  };

  const categoryIcons = {
    scan: Zap,
    leak: AlertTriangle,
    audit: Receipt,
    report: Receipt
  };

  return (
    <div className="mt-8">
      <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
        <Coins className="w-5 h-5 text-brand-green" />
        {t('account.creditsSection.creditCosts')}
      </h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {Object.entries(costsByCategory).map(([category, items]) => {
          const CategoryIcon = categoryIcons[category] || Coins;
          return (
            <div key={category} className="liquid-glass rounded-xl p-4 border border-gray-800">
              <h4 className="font-semibold mb-3 text-white flex items-center gap-2">
                <CategoryIcon className="w-4 h-4 text-brand-green" />
                {categoryLabels[category] || category}
              </h4>
              <div className="space-y-2">
                {items.map((cost) => (
                  <div key={cost.action_type} className="flex justify-between items-center py-2 border-b border-gray-800 last:border-0 gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm text-white truncate" title={cost.action_name}>{cost.action_name}</div>
                      {cost.description && (
                        <div className="text-xs text-gray-500 truncate" title={cost.description}>{cost.description}</div>
                      )}
                    </div>
                    <div className="font-bold text-brand-green whitespace-nowrap flex-shrink-0">{cost.credits_required} {t('account.creditsSection.credits')}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Transaction History (dark design)
function TransactionHistory() {
  const { t } = useI18n();
  const { transactions, loading, error, refresh } = useCreditTransactions({ limit: 100 });

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="animate-spin text-brand-green" size={32} />
      </div>
    );
  }

  if (error) {
    return <div className="p-4 bg-red-500/10 text-red-400 rounded-xl border border-red-500/30">{error}</div>;
  }

  if (transactions.length === 0) {
    return (
      <div className="liquid-glass rounded-xl p-8 border border-brand-green/20 text-center">
        <History className="w-12 h-12 text-gray-600 mx-auto mb-4" />
        <p className="text-gray-400">{t('account.creditsSection.noTransactions')}</p>
      </div>
    );
  }

  const isPositive = (type) => {
    return ['credit_monthly_renewal', 'credit_pack_purchase', 'credit_bonus', 'credit_refund'].includes(type);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold text-white flex items-center gap-2">
          <History className="w-5 h-5 text-brand-green" />
          {t('account.creditsSection.transactionHistory')}
        </h3>
        <button
          onClick={refresh}
          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-400 hover:text-white hover:bg-gray-800/50 rounded-lg transition-all"
        >
          <RefreshCw size={14} />
          {t('account.creditsSection.refresh')}
        </button>
      </div>

      <div className="liquid-glass rounded-xl border border-gray-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#111111]">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">{t('account.creditsSection.date')}</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">{t('account.creditsSection.type')}</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">{t('account.creditsSection.amount')}</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx) => (
                <tr key={tx.id} className="border-t border-gray-800 hover:bg-gray-800/30">
                  <td className="px-4 py-3 text-sm text-gray-300">
                    {new Date(tx.created_at).toLocaleString('fr-FR')}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {isPositive(tx.transaction_type) ? (
                        <ArrowUpRight className="w-4 h-4 text-brand-green flex-shrink-0" />
                      ) : (
                        <ArrowDownRight className="w-4 h-4 text-red-400 flex-shrink-0" />
                      )}
                      <span className={`text-sm px-2 py-1 rounded-lg truncate max-w-[200px] ${
                        isPositive(tx.transaction_type)
                          ? 'bg-brand-green/10 text-brand-green'
                          : 'bg-red-500/10 text-red-400'
                      }`} title={tx.transaction_type.replace('credit_', '').replace(/_/g, ' ')}>
                        {tx.transaction_type.replace('credit_', '').replace(/_/g, ' ')}
                      </span>
                    </div>
                  </td>
                  <td className={`px-4 py-3 text-right font-bold ${
                    isPositive(tx.transaction_type) ? 'text-brand-green' : 'text-red-400'
                  }`}>
                    {isPositive(tx.transaction_type) ? '+' : ''}{tx.credits_amount.toLocaleString('fr-FR')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/**
 * Main Credits Page Component
 */
export function CreditsPage() {
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dialog, setDialog] = useState({ open: false, item: null, type: null });
  const [processing, setProcessing] = useState(false);
  const { summary, loading: creditsLoading, refresh: refreshCredits } = useCredits();

  const tabs = [
    { id: 'dashboard', label: t('account.creditsSection.dashboard'), icon: LayoutDashboard },
    { id: 'plans', label: t('account.creditsSection.plansAndPacks'), icon: CreditCard },
    { id: 'history', label: t('account.creditsSection.history'), icon: History }
  ];

  const handlePlanSelect = (plan) => {
    setDialog({ open: true, item: plan, type: 'plan' });
  };

  const handlePackPurchase = (pack) => {
    setDialog({ open: true, item: pack, type: 'pack' });
  };

  const handleConfirm = async () => {
    const { item, type } = dialog;
    setProcessing(true);

    try {
      if (type === 'plan') {
        await api.post('/credits/subscribe', { plan_slug: item.slug });
        toast.success(t('account.creditsSection.planSubscribed', { plan: item.name }));
      } else {
        await api.post('/credits/purchase-pack', { pack_slug: item.slug });
        toast.success(t('account.creditsSection.packPurchased', { pack: item.name }));
      }
      await refreshCredits();
      setDialog({ open: false, item: null, type: null });
    } catch (error) {
      // Silently handle 401 errors - user will be redirected to login by API interceptor
      if (error.response?.status !== 401 && !error.isAuthRedirect) {
        toast.error(error.response?.data?.detail || t('account.creditsSection.errorOccurred'));
      }
    } finally {
      setProcessing(false);
    }
  };

  // Get plan style for sidebar
  const planSlug = summary?.subscription_plan_slug || 'free';
  const planStyle = PLAN_STYLES[planSlug] || PLAN_STYLES.free;
  const PlanIcon = planStyle.icon;

  return (
    <div className="container mx-auto px-6 py-8 flex-1 min-w-0">
      {/* Header */}
      <div className="mb-8 min-w-0">
        <h1 className="text-3xl font-bold text-white mb-2 break-words">{t('account.creditsSection.title')}</h1>
        <p className="text-gray-400 break-words">{t('account.creditsSection.subtitle')}</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar */}
        <div className="lg:w-64 flex-shrink-0">
          <div className="liquid-glass rounded-xl p-4 border border-brand-green/20 sticky top-24">
            {/* Credits Card */}
            <div className="text-center mb-6 pb-6 border-b border-gray-800">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-brand-green/30 to-brand-green/10 flex items-center justify-center border-2 border-brand-green/30">
                <Wallet className="w-8 h-8 text-brand-green" />
              </div>
              <h3 className="text-white font-semibold">
                {creditsLoading ? t('common.loading') : (summary?.total_credits || 0).toLocaleString('fr-FR')}
              </h3>
              <p className="text-gray-400 text-sm">{t('account.creditsSection.creditsAvailable')}</p>

              {/* Plan Badge */}
              <div className={`inline-flex items-center gap-2 mt-3 px-3 py-1.5 rounded-full ${planStyle.bg} ${planStyle.border} border`}>
                <PlanIcon className={`w-4 h-4 ${planStyle.color}`} />
                <span className={`text-sm font-medium ${planStyle.color}`}>
                  {summary?.subscription_plan_name || t('account.creditsSection.free')}
                </span>
              </div>
            </div>

            {/* Navigation */}
            <nav className="space-y-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                      activeTab === tab.id
                        ? 'bg-brand-green/10 text-brand-green border border-brand-green/30'
                        : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
                    }`}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <span className="font-medium text-sm truncate">{tab.label}</span>
                    {activeTab === tab.id && (
                      <ChevronRight className="w-4 h-4 ml-auto" />
                    )}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          <div className="liquid-glass rounded-xl border border-brand-green/20 p-6">
            {/* Dashboard Tab */}
            {activeTab === 'dashboard' && (
              <div>
                <CreditSummaryCard summary={summary} loading={creditsLoading} />
                <CostsSection />
              </div>
            )}

            {/* Plans & Packs Tab */}
            {activeTab === 'plans' && (
              <div>
                <PlansSection currentPlan={summary?.subscription_plan_slug} onSelect={handlePlanSelect} />
                <PacksSection onPurchase={handlePackPurchase} />
              </div>
            )}

            {/* History Tab */}
            {activeTab === 'history' && (
              <TransactionHistory />
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Dialog (dark design) */}
      {dialog.open && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="liquid-glass rounded-xl border border-brand-green/30 max-w-md w-full mx-4 p-6">
            <h3 className="text-xl font-bold text-white mb-4">
              {dialog.type === 'plan' ? t('account.creditsSection.confirmPlanChange') : t('account.creditsSection.confirmPurchase')}
            </h3>

            {dialog.item && (
              <div className="mb-6">
                <div className="text-lg font-semibold text-white">{dialog.item.name}</div>
                <div className="text-2xl font-bold text-brand-green my-2">
                  {dialog.type === 'plan'
                    ? `${dialog.item.monthly_credits.toLocaleString('fr-FR')} ${t('account.creditsSection.creditsPerMonth')}`
                    : `${dialog.item.credits.toLocaleString('fr-FR')} ${t('account.creditsSection.credits')}`
                  }
                </div>
                <div className="text-lg text-white">
                  {dialog.type === 'plan'
                    ? (dialog.item.monthly_price_eur === 0 ? t('account.creditsSection.free') : `${dialog.item.monthly_price_eur}€${t('account.creditsSection.perMonth')}`)
                    : `${dialog.item.price_eur.toLocaleString('fr-FR')}€`
                  }
                </div>
                {dialog.item.description && (
                  <p className="text-sm text-gray-400 mt-2">{dialog.item.description}</p>
                )}
              </div>
            )}

            <div className="bg-brand-green/10 border border-brand-green/30 rounded-xl p-3 mb-6 text-sm text-brand-green">
              {dialog.type === 'plan'
                ? t('account.creditsSection.planActiveImmediately')
                : t('account.creditsSection.creditsAddedImmediately')
              }
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setDialog({ open: false, item: null, type: null })}
                disabled={processing}
                className="flex-1 py-3 border border-gray-700 rounded-xl text-gray-300 hover:bg-gray-800/50 disabled:opacity-50 transition-all"
              >
                {t('account.creditsSection.cancel')}
              </button>
              <button
                onClick={handleConfirm}
                disabled={processing}
                className="flex-1 py-3 bg-brand-green text-black rounded-xl hover:bg-brand-green/90 disabled:opacity-50 flex items-center justify-center gap-2 transition-all font-medium"
              >
                {processing && <Loader2 className="animate-spin" size={16} />}
                {processing ? t('account.creditsSection.processing') : t('account.creditsSection.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreditsPage;
